<template>
  <div class="global-search">
    <div class="bg-decoration">
      <div class="orb orb-1"></div>
      <div class="orb orb-2"></div>
      <div class="orb orb-3"></div>
      <svg class="grid-lines" viewBox="0 0 1200 700" fill="none">
        <path d="M0 120H1200" />
        <path d="M0 260H1200" />
        <path d="M0 400H1200" />
        <path d="M0 540H1200" />
      </svg>
    </div>

    <div class="content">
      <section class="hero">
        <div class="eyebrow">Cross-module discovery</div>
        <h1 class="page-title">Global Search</h1>
        <p class="page-subtitle">
          Choose a keyword from the long dropdown, inspect its summary statistics, and review matched results across
          E-M-D, G-M-D, and M-M-D tabs.
        </p>
      </section>

      <section class="search-panel">
        <div class="panel-header">
          <div>
            <h2>Select keyword</h2>
            <p>Use the dropdown to switch the statistics and search results below.</p>
          </div>
        </div>

        <el-select
          v-model="selectedDatasetId"
          filterable
          :loading="loadingKeywords"
          placeholder="Search and choose a keyword"
          class="dataset-select"
          popper-class="dataset-select-popper"
          @change="handleDatasetChange"
        >
          <el-option
            v-for="keyword in keywordOptions"
            :key="keyword"
            :label="keyword"
            :value="keyword"
          />
        </el-select>
        <div v-if="keywordError" class="panel-error">{{ keywordError }}</div>
      </section>

      <section v-if="selectedDataset" class="summary-section">
        <div class="section-head">
          <div>
            <!-- <div class="section-kicker">Summary Statistics</div> -->
            <h2>{{ selectedKeyword }}</h2>
          </div>
          <!-- <div class="section-meta">{{ summaryNote }}</div> -->
        </div>

        <div v-if="summaryFields.length > 0" class="stats-grid">
          <article v-for="field in summaryFields" :key="field.key" class="stat-card">
            <div class="stat-label">{{ field.label }}</div>
            <div class="stat-value">{{ field.value }}</div>
            <div class="stat-note">{{ field.note }}</div>
          </article>
        </div>
        <div v-else class="summary-empty">
          No summary statistics returned for this keyword.
        </div>
      </section>

      <section v-if="selectedDataset" class="results-section">
        <div class="section-head">
          <div>
            <div class="section-kicker">Search Results</div>
            <h2>Matched Pathways</h2>
          </div>
          <div class="section-meta">Results are grouped by pathway.</div>
        </div>

        <el-tabs v-model="activeTab" class="results-tabs">
          <el-tab-pane
            v-for="tab in tabItems"
            :key="tab.key"
            :label="tab.label"
            :name="tab.key"
          >
            <div class="result-panel">
              <div class="result-toolbar">
                <div class="result-summary">
                  <h3>{{ tab.label }} Results</h3>
                  <p>First {{ currentRows.length }} rows loaded</p>
                </div>
                <div class="result-actions">
                  <a
                    v-if="currentResultSet.downloadUrl"
                    :href="currentResultSet.downloadUrl"
                    class="download-btn"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <span class="download-icon">↓</span>
                    <span class="download-text">Download file</span>
                  </a>
                </div>
              </div>

              <div v-if="loadingMatches && activeTab === tab.key" class="result-loading">Loading results...</div>
              <div v-else class="result-table-wrap">
                <el-table
                  :data="pagedRows"
                  border
                  stripe
                  class="result-table"
                  :empty-text="emptyTableText"
                >
                  <el-table-column
                    v-for="column in currentColumns"
                    :key="column.key"
                    :prop="column.key"
                    :label="column.label"
                    :min-width="column.minWidth"
                    show-overflow-tooltip
                  >
                    <template slot-scope="scope">
                      <MetaboliteTooltip
                        v-if="isMetaboliteColumn(column)"
                        :value="scope.row[column.key]"
                      />
                      <span v-else>{{ scope.row[column.key] }}</span>
                    </template>
                  </el-table-column>
                </el-table>
                <div class="result-footer">
                  <div class="result-footnote">
                    Showing {{ pageRangeStart }}-{{ pageRangeEnd }} of {{ currentRows.length }} rows
                  </div>
                  <el-pagination
                    background
                    layout="prev, pager, next"
                    :current-page="getTabPage(tab.key)"
                    :page-size="pageSize"
                    :total="currentRows.length"
                    :hide-on-single-page="true"
                    @current-change="page => setTabPage(tab.key, page)"
                  />
                </div>
              </div>
            </div>
          </el-tab-pane>
        </el-tabs>
      </section>

      <section v-else class="empty-state">
        <div class="empty-card">
          <h2>Pick a dataset to begin</h2>
          <p>
            Summary statistics and the three result tabs will appear after you select an item from the dropdown.
          </p>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import request from '@/utils/request'
import MetaboliteTooltip from '@/components/MetaboliteTooltip.vue'

const DEFAULT_KEYWORD = 'Type II diabetes'

export default {
  name: 'GlobalSearch',
  components: {
    MetaboliteTooltip
  },
  data() {
    return {
      selectedDatasetId: (this.$route.query && this.$route.query.keyword) || DEFAULT_KEYWORD,
      activeTab: 'emd',
      loadingKeywords: false,
      loadingOverview: false,
      loadingMatches: false,
      loadingQueryId: false,
      keywordError: '',
      keywordOptions: [],
      summaryData: null,
      queryIdData: null,
      hasPhdTabs: false,
      pageSize: 10,
      tabPages: {
        emd: 1,
        gmd: 1,
        mmd: 1,
        ed: 1,
        md: 1,
        gd: 1
      },
      matchesData: {
        emd: { downloadUrl: '', headers: [], lines: [] },
        gmd: { downloadUrl: '', headers: [], lines: [] },
        mmd: { downloadUrl: '', headers: [], lines: [] },
        ed: { downloadUrl: '', headers: [], lines: [] },
        md: { downloadUrl: '', headers: [], lines: [] },
        gd: { downloadUrl: '', headers: [], lines: [] }
      }
    }
  },
  created() {
    this.fetchKeywords()
  },
  computed: {
    selectedKeyword() {
      return this.selectedDatasetId || ''
    },
    selectedDataset() {
      if (!this.selectedKeyword) return null
      return {
        label: this.selectedKeyword,
        summary: this.summaryData,
        results: this.matchesData
      }
    },
    summaryNote() {
      if (!this.summaryData) return 'No summary data loaded yet.'
      return this.summaryData.note || this.summaryData.summary || 'Summary statistics'
    },
    summaryFields() {
      const summary = this.summaryData
      if (!summary || typeof summary !== 'object' || Array.isArray(summary)) return []

      return Object.keys(summary)
        .filter(key => key !== 'note' && key !== 'summary')
        .map(key => ({
          key,
          label: key,
          value: this.formatSummaryValue(summary[key]),
          note: this.getSummaryNote(key)
        }))
    },
    tabItems() {
      const items = [
        { key: 'emd', label: 'E-M-D' },
        { key: 'gmd', label: 'G-M-D' },
        { key: 'mmd', label: 'M-M-D' }
      ]

      if (this.hasPhdTabs) {
        items.push(
          { key: 'ed', label: 'E-D' },
          { key: 'md', label: 'M-D' },
          { key: 'gd', label: 'G-D' }
        )
      }

      return items
    },
    currentResultSet() {
      return this.matchesData[this.activeTab] || { downloadUrl: '', headers: [], lines: [] }
    },
    currentHeaders() {
      const headers = Array.isArray(this.currentResultSet.headers) ? this.currentResultSet.headers : []
      if (headers.length > 0) return headers

      const firstLine = Array.isArray(this.currentResultSet.lines) && this.currentResultSet.lines.length > 0
        ? this.currentResultSet.lines[0]
        : null
      if (firstLine && typeof firstLine === 'object' && !Array.isArray(firstLine)) {
        return Object.keys(firstLine)
      }
      if (Array.isArray(firstLine)) {
        return firstLine.map((_, index) => `Column ${index + 1}`)
      }
      return []
    },
    currentColumns() {
      const headers = this.currentHeaders
      return headers.map((header, index) => ({
        key: this.headerKey(header, index),
        label: header,
        minWidth: 160
      }))
    },
    currentRows() {
      const headers = this.currentHeaders
      const lines = Array.isArray(this.currentResultSet.lines) ? this.currentResultSet.lines : []
      return lines.map((line, rowIndex) => this.normalizeRow(line, headers, rowIndex))
    },
    pagedRows() {
      const start = (this.currentPage - 1) * this.pageSize
      return this.currentRows.slice(start, start + this.pageSize)
    },
    currentPage() {
      return this.getTabPage(this.activeTab)
    },
    pageRangeStart() {
      if (this.currentRows.length === 0) return 0
      return (this.currentPage - 1) * this.pageSize + 1
    },
    pageRangeEnd() {
      return Math.min(this.currentPage * this.pageSize, this.currentRows.length)
    },
    emptyTableText() {
      return 'No rows returned.'
    },
    currentTabLabel() {
      if (this.activeTab === 'emd') return 'E-M-D'
      if (this.activeTab === 'gmd') return 'G-M-D'
      if (this.activeTab === 'mmd') return 'M-M-D'
      if (this.activeTab === 'ed') return 'E-D'
      if (this.activeTab === 'md') return 'M-D'
      return 'G-D'
    },
    resultCountText() {
      return `${this.currentRows.length} rows loaded`
    },
    activeTabLabel() {
      return `${this.currentTabLabel} Results`
    }
  },
  methods: {
    async fetchKeywords() {
      this.loadingKeywords = true
      this.keywordError = ''
      try {
        const list = await request.get('/api/search/keywords')
        const options = Array.isArray(list) ? list : []
        const requestedKeyword = this.selectedDatasetId
        const optionsWithRequestedKeyword = requestedKeyword && !options.includes(requestedKeyword)
          ? [requestedKeyword].concat(options)
          : options
        this.keywordOptions = optionsWithRequestedKeyword.includes(DEFAULT_KEYWORD)
          ? optionsWithRequestedKeyword
          : [DEFAULT_KEYWORD].concat(optionsWithRequestedKeyword.filter(item => item !== DEFAULT_KEYWORD))

        if (!this.keywordOptions.includes(this.selectedDatasetId)) {
          this.selectedDatasetId = this.keywordOptions[0] || DEFAULT_KEYWORD
        }
        await this.handleDatasetChange()
      } catch (error) {
        this.keywordError = 'Failed to load search keywords.'
      } finally {
        this.loadingKeywords = false
      }
    },
    async handleDatasetChange() {
      this.activeTab = 'emd'
      this.resetTabPages()
      if (!this.selectedKeyword) return
      await Promise.all([
        this.fetchQueryId(this.selectedKeyword),
        this.fetchOverview(this.selectedKeyword),
        this.fetchMatches(this.selectedKeyword, 'E-M-D'),
        this.fetchMatches(this.selectedKeyword, 'G-M-D'),
        this.fetchMatches(this.selectedKeyword, 'M-M-D')
      ])
    },
    async fetchOverview(keyword) {
      this.loadingOverview = true
      try {
        const data = await request.get('/api/search/overview', {
          params: { keyword }
        })
        this.summaryData = data || {}
      } catch (error) {
        this.summaryData = null
        this.keywordError = this.keywordError || 'Failed to load summary statistics.'
      } finally {
        this.loadingOverview = false
      }
    },
    async fetchQueryId(keyword) {
      this.loadingQueryId = true
      try {
        const data = await request.get('/api/search/queryId', {
          params: { keyword }
        })
        this.queryIdData = data || null
        this.hasPhdTabs = this.containsPhdValue(data)
        this.resetPhdMatches()
        if (this.hasPhdTabs) {
          await Promise.all([
            this.fetchMatches(keyword, 'E-D'),
            this.fetchMatches(keyword, 'M-D'),
            this.fetchMatches(keyword, 'G-D')
          ])
        }
      } catch (error) {
        this.queryIdData = null
        this.hasPhdTabs = false
      } finally {
        this.loadingQueryId = false
      }
    },
    formatSummaryValue(value) {
      if (value === null || value === undefined || value === '') return '--'
      if (typeof value === 'object') return JSON.stringify(value)
      return String(value)
    },
    getSummaryNote(key) {
      if (key === 'Total.Matches') {
        return 'All matches for the selected keyword'
      }
      return `Significant signal in the ${key} bucket`
    },
    normalizeMatchItem(item, index, type) {
      if (typeof item === 'string') {
        return {
          id: `${type}-${index}`,
          title: item,
          description: item,
          badge: type,
          meta1: 'Keyword match',
          meta2: 'Text result',
          meta3: 'API string'
        }
      }

      return {
        id: item.id || `${type}-${index}`,
        title: item.title || item.name || item.label || item.keyword || `Result ${index + 1}`,
        description: item.description || item.desc || item.summary || '',
        badge: item.score || item.pValue || item.pvalue || type,
        meta1: item.meta1 || item.field1 || item.category || type,
        meta2: item.meta2 || item.field2 || item.subcategory || '',
        meta3: item.meta3 || item.field3 || item.source || ''
      }
    },
    headerKey(header, index) {
      return String(header || `col_${index}`)
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '') || `col_${index}`
    },
    isMetaboliteColumn(column) {
      const value = `${column.key || ''} ${column.label || ''}`.toLowerCase()
      return value.includes('metabolite')
    },
    normalizeRow(line, headers, rowIndex) {
      if (Array.isArray(line)) {
        const row = {}
        headers.forEach((header, index) => {
          row[this.headerKey(header, index)] = line[index] !== undefined && line[index] !== null ? line[index] : ''
        })
        return row
      }

      if (line && typeof line === 'object') {
        const row = {}
        headers.forEach((header, index) => {
          const key = this.headerKey(header, index)
          if (line[header] !== undefined && line[header] !== null) {
            row[key] = line[header]
            return
          }
          const altKey = String(header)
            .replace(/\s+/g, '_')
            .replace(/\./g, '_')
          row[key] = line[altKey] !== undefined && line[altKey] !== null ? line[altKey] : ''
        })
        return row
      }

      const row = {}
      headers.forEach((header, index) => {
        row[this.headerKey(header, index)] = index === 0 ? line : ''
      })
      if (!headers.length) {
        row[`col_${rowIndex}`] = line
      }
      return row
    },
    normalizeMatchResponse(data) {
      if (!data) {
        return { downloadUrl: '', headers: [], lines: [] }
      }
      if (Array.isArray(data)) {
        return { downloadUrl: '', headers: [], lines: data }
      }

      return {
        downloadUrl: data.downloadUrl || data.download_url || '',
        headers: Array.isArray(data.headers) ? data.headers : [],
        lines: Array.isArray(data.lines) ? data.lines : []
      }
    },
    containsPhdValue(data) {
      const values = Array.isArray(data)
        ? data
        : data && typeof data === 'object'
          ? Object.values(data)
          : []
      return values.some(value => typeof value === 'string' && value.indexOf('PHD') === 0)
    },
    resetPhdMatches() {
      this.$set(this.matchesData, 'ed', { downloadUrl: '', headers: [], lines: [] })
      this.$set(this.matchesData, 'md', { downloadUrl: '', headers: [], lines: [] })
      this.$set(this.matchesData, 'gd', { downloadUrl: '', headers: [], lines: [] })
      this.setTabPage('ed', 1)
      this.setTabPage('md', 1)
      this.setTabPage('gd', 1)
    },
    async fetchMatches(keyword, type) {
      const key = this.typeToKey(type)
      this.loadingMatches = true
      try {
        const data = await request.get('/api/search/matches', {
          params: { keyword, type }
        })
        this.$set(this.matchesData, key, this.normalizeMatchResponse(data))
        this.setTabPage(key, 1)
      } catch (error) {
        this.$set(this.matchesData, key, { downloadUrl: '', headers: [], lines: [] })
        this.keywordError = this.keywordError || `Failed to load ${type} matches.`
      } finally {
        this.loadingMatches = false
      }
    },
    getTabPage(tabKey) {
      return this.tabPages[tabKey] || 1
    },
    setTabPage(tabKey, page) {
      this.$set(this.tabPages, tabKey, page)
    },
    resetTabPages() {
      this.setTabPage('emd', 1)
      this.setTabPage('gmd', 1)
      this.setTabPage('mmd', 1)
    },
    typeToKey(type) {
      if (type === 'E-M-D') return 'emd'
      if (type === 'G-M-D') return 'gmd'
      if (type === 'M-M-D') return 'mmd'
      if (type === 'E-D') return 'ed'
      if (type === 'M-D') return 'md'
      if (type === 'G-D') return 'gd'
      return 'mmd'
    }
  }
}
</script>

<style lang="less" scoped>
.global-search {
  min-height: 100%;
  position: relative;
  overflow: hidden;
  background:
    radial-gradient(circle at top left, rgba(220, 38, 38, 0.16), transparent 32%),
    linear-gradient(180deg, #fff7ed 0%, #ffffff 46%, #fffefc 100%);
}

.bg-decoration {
  position: absolute;
  inset: 0;
  pointer-events: none;
  overflow: hidden;
}

.orb {
  position: absolute;
  border-radius: 999px;
  filter: blur(4px);
  opacity: 0.45;
}

.orb-1 {
  width: 260px;
  height: 260px;
  top: -80px;
  right: -40px;
  background: radial-gradient(circle, rgba(251, 146, 60, 0.45), rgba(251, 146, 60, 0));
}

.orb-2 {
  width: 180px;
  height: 180px;
  left: -50px;
  top: 280px;
  background: radial-gradient(circle, rgba(239, 68, 68, 0.22), rgba(239, 68, 68, 0));
}

.orb-3 {
  width: 320px;
  height: 320px;
  right: 10%;
  bottom: -120px;
  background: radial-gradient(circle, rgba(251, 191, 36, 0.2), rgba(251, 191, 36, 0));
}

.grid-lines {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  opacity: 0.18;
}

.grid-lines path {
  stroke: #fb923c;
  stroke-width: 1;
}

.content {
  position: relative;
  z-index: 1;
  margin: 0 auto;
  padding: 48px 32px 56px;
}

.hero {
  margin-bottom: 28px;
}

.eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  border-radius: 999px;
  background: rgba(249, 115, 22, 0.1);
  color: #c2410c;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  margin-bottom: 14px;
}

.page-title {
  font-size: 44px;
  line-height: 1.1;
  color: #111827;
  margin: 0 0 14px 0;
  font-weight: 800;
}

.page-subtitle {
  font-size: 17px;
  line-height: 1.7;
  color: #6b7280;
}

.search-panel,
.summary-section,
.results-section,
.empty-state {
  background: rgba(255, 255, 255, 0.82);
  border: 1px solid rgba(251, 146, 60, 0.12);
  box-shadow: 0 20px 60px rgba(120, 53, 15, 0.08);
  backdrop-filter: blur(10px);
  border-radius: 24px;
}

.search-panel {
  padding: 26px;
  margin-bottom: 22px;
}

.panel-header {
  display: flex;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 18px;
}

.panel-header h2,
.section-head h2,
.empty-card h2 {
  font-size: 22px;
  line-height: 1.25;
  color: #111827;
  margin: 0 0 6px 0;
}

.panel-header p,
.section-meta,
.empty-card p {
  color: #6b7280;
  line-height: 1.6;
}

.panel-error {
  margin-top: 12px;
  color: #dc2626;
  font-size: 13px;
}

.dataset-select {
  width: 100%;
}

.summary-section,
.results-section {
  padding: 26px;
  margin-bottom: 22px;
}

.section-head {
  display: flex;
  justify-content: space-between;
  gap: 20px;
  align-items: flex-start;
  margin-bottom: 18px;
}

.section-kicker {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #c2410c;
  margin-bottom: 8px;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 16px;
}

.stat-card {
  padding: 18px;
  border-radius: 20px;
  background: linear-gradient(180deg, #fff7ed 0%, #ffffff 100%);
  border: 1px solid rgba(251, 146, 60, 0.12);
}

.stat-label {
  font-size: 13px;
  color: #9a3412;
  font-weight: 700;
  margin-bottom: 10px;
}

.stat-value {
  font-size: 28px;
  line-height: 1.1;
  color: #111827;
  font-weight: 800;
  margin-bottom: 8px;
}

.stat-note {
  font-size: 13px;
  line-height: 1.55;
  color: #6b7280;
}

.summary-empty {
  padding: 18px 0 4px;
  color: #6b7280;
  font-size: 14px;
}

.results-tabs {
  margin-top: 6px;
}

.result-list {
  display: grid;
  gap: 14px;
}

.result-card {
  padding: 18px 20px;
  border-radius: 18px;
  border: 1px solid rgba(251, 146, 60, 0.14);
  background: #fff;
}

.result-title-row {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: flex-start;
  margin-bottom: 8px;
}

.result-title-row h3 {
  font-size: 17px;
  line-height: 1.35;
  color: #111827;
  margin: 0;
}

.score-tag {
  flex-shrink: 0;
  padding: 6px 10px;
  border-radius: 999px;
  background: rgba(249, 115, 22, 0.1);
  color: #9a3412;
  font-size: 12px;
  font-weight: 700;
}

.result-desc {
  color: #4b5563;
  line-height: 1.65;
  margin-bottom: 12px;
}

.result-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  color: #7c2d12;
  font-size: 12px;
  font-weight: 600;
}

.result-meta span {
  padding: 6px 10px;
  border-radius: 999px;
  background: #fff7ed;
}

.result-table-wrap {
  border-radius: 18px;
  overflow: hidden;
}

.result-table {
  width: 100%;
}

.result-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 18px;
}

.result-summary {
  min-width: 0;
}

.result-actions {
  display: flex;
  justify-content: flex-end;
  align-items: flex-start;
  flex-shrink: 0;
}

.result-footer {
  margin-top: 14px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

.result-footnote {
  color: #6b7280;
  font-size: 13px;
}

.result-loading {
  padding: 40px 0;
  text-align: center;
  color: #6b7280;
  font-weight: 600;
}

.download-btn {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 11px 16px;
  border-radius: 14px;
  background: linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%);
  color: #9a3412;
  font-size: 14px;
  font-weight: 700;
  text-decoration: none;
  border: 1px solid rgba(249, 115, 22, 0.18);
  box-shadow: 0 10px 24px rgba(251, 146, 60, 0.12);
  transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
}

.download-btn:hover {
  transform: translateY(-1px);
  background: linear-gradient(135deg, #ffedd5 0%, #fed7aa 100%);
  box-shadow: 0 14px 28px rgba(251, 146, 60, 0.18);
}

.download-icon {
  display: inline-flex;
  width: 22px;
  height: 22px;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  background: rgba(249, 115, 22, 0.12);
  color: #c2410c;
  font-size: 13px;
  line-height: 1;
}

.download-text {
  white-space: nowrap;
}

.results-tabs::v-deep .el-tabs__nav-wrap::after {
  background-color: rgba(153, 27, 27, 0.14);
}

.results-tabs::v-deep .el-tabs__item {
  color: #6b7280;
  font-weight: 600;
}

.results-tabs::v-deep .el-tabs__item:hover {
  color: #b91c1c;
}

.results-tabs::v-deep .el-tabs__item.is-active {
  color: #991b1b;
}

.results-tabs::v-deep .el-tabs__active-bar {
  background-color: #991b1b;
}

.results-tabs::v-deep .el-tabs__header {
  margin: 0 0 18px;
}

.results-tabs::v-deep .el-pagination.is-background .btn-prev,
.results-tabs::v-deep .el-pagination.is-background .btn-next,
.results-tabs::v-deep .el-pagination.is-background .el-pager li {
  background-color: #fff7f7;
  color: #7f1d1d;
  border-radius: 10px;
}

.results-tabs::v-deep .el-pagination.is-background .el-pager li:not(.disabled).active {
  background-color: #991b1b;
  color: #ffffff;
}

.results-tabs::v-deep .el-pagination.is-background .el-pager li:not(.disabled):hover {
  color: #991b1b;
}

.results-tabs::v-deep .el-pagination button:hover {
  color: #991b1b;
}

.empty-state {
  padding: 32px;
}

.empty-card {
  min-height: 220px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
}

@media (max-width: 1100px) {
  .stats-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 768px) {
  .content {
    padding: 28px 16px 40px;
  }

  .page-title {
    font-size: 34px;
  }

  .panel-header,
  .section-head,
  .result-title-row {
    flex-direction: column;
  }

  .stats-grid {
    grid-template-columns: 1fr;
  }

  .search-panel,
  .summary-section,
  .results-section,
  .empty-state {
    border-radius: 18px;
    padding: 18px;
  }
}
</style>
